/**
 * SOUND-776 — Transmit a MID application to Kurv (Stratus).
 *
 * Every decision the transmit dialog makes lives here, with tests, because
 * sound-app has no component-test harness (SOUND-679) and the dominant bug class
 * in this repo is a shape mismatch across a boundary that fails silently. The
 * component only renders what these functions return.
 *
 * API (sound-api routes/stratus-transmit.mjs):
 *   POST /integrations/stratus/transmit/:applicationId           dry run
 *   POST /integrations/stratus/transmit/:applicationId?live=true  transmit
 *        body { mccId, mcc, mccDescription, sendForSignature }
 *   GET  /integrations/stratus/mcc?search=
 *
 * FIELD KEYS. The preflight's `missing[].field` / `warningDetail[].field` are the
 * Stratus payload's own snake_case names, produced by services/stratusMapper.mjs.
 * The PRIMARY owner's fields carry no prefix (`title`, `first_name`, …); every
 * additional owner's are prefixed `owners[<ownerId>].` — so `title` alone means
 * the primary owner, not the business.
 */

// ── Who sees the action ────────────────────────────────────────────────────

/** Same test the API applies before a live send (`/kurv/i` on the name). */
export function isKurvProcessor(processorName) {
  return typeof processorName === "string" && /kurv/i.test(processorName);
}

/** `externalApplicationId` set means it has left the building. */
export function isTransmitted(application) {
  return Boolean(application && application.externalApplicationId);
}

/**
 * Should the "Transmit to Kurv" action render?
 * Internal + Kurv + not yet transmitted. Anything else gets no button — a 400
 * from the API for a non-Kurv application is a defect, not a user path.
 */
export function canShowTransmit({ isInternal, processorName, application } = {}) {
  return Boolean(isInternal) && isKurvProcessor(processorName) && Boolean(application) && !isTransmitted(application);
}

// ── Field labels ───────────────────────────────────────────────────────────

/** Business-level and owner-level Stratus fields → what a person calls them. */
export const FIELD_LABELS = {
  // Business
  legal_business_name: "Legal business name",
  tax_ein: "Tax ID (EIN)",
  legal_address: "Legal business address",
  type_of_ownership: "Ownership type",
  years_of_ownership: "Length of ownership",
  premise_type: "Premise type",
  business_email: "Business email",
  business_phone: "Business phone",
  business_website: "Business website",
  dba_name: "DBA name",
  dba_address: "DBA address",
  mcc_id: "MCC",
  bank_name: "Bank name",
  routing_number: "Bank routing number",
  account_number: "Bank account number",
  max_monthly_sales: "Monthly volume",
  highest_sale_amount: "Highest ticket",
  average_sale_amount: "Average ticket",
  sales_swiped_percentage: "Transaction-type percentages",
  channel_mix: "Transaction-type percentages",
  ways_to_accept_cards: "Ways to accept cards",
  product_services: "Products / services sold",
  terminated_by_vmda: "Ever terminated (risk question)",
  is_pci_compliant: "PCI compliant (risk question)",
  data_compromise_investigations: "Data compromise investigations (risk question)",
  visa_risk_programs: "Visa risk programs (risk question)",
  pricing_template_id: "Pricing template",
  owners: "Owners",
  // Not payload fields — transmit-level blockers the route adds itself.
  currentStatus: "Application status",
  transmit: "Transmit already in progress",
  // Owner (primary when unprefixed)
  first_name: "Name (first / last)",
  last_name: "Last name",
  email: "Email",
  phone: "Phone",
  address: "Home address",
  years_of_residence: "Time at current residence",
  social_security: "SSN / ITIN",
  date_of_birth: "Date of birth",
  drivers_license_state: "Driver's licence state",
  country: "Passport country",
  id_number: "ID document",
  ownership_percentage: "Ownership percentage",
  title: "Job title",
};

/** Keys that only exist at the owner level, so an unprefixed one is the primary owner. */
const OWNER_KEYS = new Set([
  "first_name",
  "last_name",
  "email",
  "phone",
  "address",
  "years_of_residence",
  "social_security",
  "date_of_birth",
  "drivers_license_state",
  "country",
  "id_number",
  "title",
]);

/**
 * Split a mapper field key into its owner (if any) and base key.
 *   "owners[abc].title" → { ownerId: "abc", key: "title", owner: "additional" }
 *   "title"             → { ownerId: null,  key: "title", owner: "primary" }
 *   "tax_ein"           → { ownerId: null,  key: "tax_ein", owner: null }
 *
 * `ownership_percentage` unprefixed is the COMBINED ownership check, which is a
 * business-level problem, so it is not attributed to the primary owner.
 */
export function parseFieldKey(field) {
  const s = String(field || "");
  const m = /^owners\[([^\]]+)\]\.(.+)$/.exec(s);
  if (m) return { ownerId: m[1], key: m[2], owner: "additional" };
  if (OWNER_KEYS.has(s)) return { ownerId: null, key: s, owner: "primary" };
  return { ownerId: null, key: s, owner: null };
}

function humanizeKey(key) {
  const s = String(key || "").replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Unknown field";
}

/**
 * A field key → a label a person understands, naming the owner when there is one.
 * @param {string} field
 * @param {Array<{ownerId, fullName}>} ownerNames  preview.ownerNames from the dry run
 */
export function fieldLabel(field, ownerNames = []) {
  const { ownerId, key, owner } = parseFieldKey(field);
  const base = FIELD_LABELS[key] || humanizeKey(key);
  if (!owner) return base;
  let who;
  if (owner === "primary") {
    const name = ownerNames?.[0]?.fullName;
    who = name ? `Primary owner (${name})` : "Primary owner";
  } else {
    const name = (ownerNames || []).find((o) => o && o.ownerId === ownerId)?.fullName;
    who = name ? `Owner ${name}` : "Additional owner";
  }
  return `${who}: ${base.charAt(0).toLowerCase()}${base.slice(1)}`;
}

/**
 * Replacement wording for reasons whose API text is written for engineers or
 * points at the wrong fix. Anything not listed shows the API's own reason with
 * code formatting stripped.
 */
const PLAIN_REASONS = {
  // currentStatus / transmit keep the API's reason: it names the actual status
  // or the time the stuck transmit started, which is the useful part.
  mcc_id: "Pick a merchant category (MCC) below.",
  // SOUND-784: pricing_template_id is NOT overridden — the API has two
  // reasons ("no active template — add one on the processor page" and "no
  // default — choose one in the transmit dialog") and its own wording is the
  // right instruction for each.
  title: "Set this owner's Job Title (Kurv) on the client's Registration tab.",
};

/** Strip backticks and collapse whitespace — no code formatting in front of ops. */
export function plainText(s) {
  return String(s ?? "").replace(/`/g, "").replace(/\s+/g, " ").trim();
}

export function plainReason(field, reason) {
  const { key } = parseFieldKey(field);
  return PLAIN_REASONS[key] || plainText(reason);
}

/** Is this warning the owner first/last name split the CPO wants visible? */
export function isNameSplitWarning(item) {
  return parseFieldKey(item?.field).key === "first_name";
}

// ── The preflight view ─────────────────────────────────────────────────────

/**
 * SOUND-776 (CPO, live test) — MCC and pricing template are REQUIRED FIELDS of
 * a Kurv transmit, not data errors. The dry run keeps reporting them in
 * `missing` (the API is the guard), but the dialog renders them as form fields
 * with an inline hint, never in the red blocking list. The blocking list is for
 * genuine data problems only (owner title, DBA, premise type, …).
 */
export const SELECTION_FIELDS = ["mcc_id", "pricing_template_id"];
export const MCC_REQUIRED_HINT = "Required — search and pick a merchant category.";

export function isSelectionField(field) {
  return SELECTION_FIELDS.includes(parseFieldKey(field).key) && parseFieldKey(field).owner === null;
}

/**
 * Turn a dry-run (or a 422 refusal, which carries the same summary) into what
 * the panel renders. Never throws on a partial body.
 *
 * @returns {{
 *   blocking: Array<{field,label,detail}>,      data problems only
 *   warnings: Array<{field,label,detail,nameSplit}>,
 *   nameSplitCount: number,
 *   selectionNeeds: {
 *     mcc: string|null,                          API reason while unpicked
 *     pricingTemplate: { reason, href } | null,  hint + processor-page link
 *   },
 *   canSubmit: boolean,                          server agrees AND no data problem
 * }}
 */
export function buildPreflightView(result, { processorHref = null } = {}) {
  const ownerNames = result?.preview?.ownerNames || [];
  const missing = Array.isArray(result?.missing) ? result.missing : [];
  const warns = Array.isArray(result?.warningDetail) ? result.warningDetail : [];

  const selection = missing.filter((m) => isSelectionField(m?.field));
  const data = missing.filter((m) => !isSelectionField(m?.field));
  const mccNeed = selection.find((m) => m.field === "mcc_id");
  const templateNeed = selection.find((m) => m.field === "pricing_template_id");

  const blocking = data.map((m) => ({
    field: m.field,
    label: fieldLabel(m.field, ownerNames),
    detail: plainReason(m.field, m.reason),
  }));
  const warnings = warns.map((w) => ({
    field: w.field,
    label: fieldLabel(w.field, ownerNames),
    detail: plainText(w.detail),
    nameSplit: isNameSplitWarning(w),
  }));
  // Name-split warnings first: the CPO asked for them to be seen, and a long
  // list of asserted processor defaults would otherwise bury them.
  warnings.sort((a, b) => Number(b.nameSplit) - Number(a.nameSplit));

  return {
    blocking,
    warnings,
    nameSplitCount: warnings.filter((w) => w.nameSplit).length,
    selectionNeeds: {
      // The API's mcc reason is written for engineers; the hint is not.
      mcc: mccNeed ? MCC_REQUIRED_HINT : null,
      // The API's two reasons ("no active template — add one on the processor
      // page", "no default — choose one") ARE the hint under the field.
      pricingTemplate: templateNeed
        ? { reason: plainText(templateNeed.reason) || "Required", href: processorHref }
        : null,
    },
    // `wouldSubmit` is the server's verdict and must be EXPLICITLY true — a
    // partial or unexpected body never unlocks a live send. An empty blocking
    // list must agree with it.
    canSubmit: result?.wouldSubmit === true && blocking.length === 0,
  };
}

/**
 * May the operator press "Review & transmit"? Both required selections made,
 * no data problem, and the server's latest dry run (which was sent those
 * selections) agrees.
 */
export function canTransmit(view, { mccSelected, templateSelected } = {}) {
  return Boolean(view?.canSubmit) && Boolean(mccSelected) && Boolean(templateSelected);
}

/**
 * Merge a 422 refusal into the last dry run. The refusal carries a fresh
 * missing/warning list but no `preview`, so keep the preview we already had.
 */
export function mergeRefusal(previous, refusal) {
  return {
    ...(previous || {}),
    ...(refusal || {}),
    preview: refusal?.preview || previous?.preview || null,
    wouldSubmit: false,
  };
}

// ── MCC picker ─────────────────────────────────────────────────────────────

/**
 * Normalise the MCC search response `{ records: [{ id, mcc, category,
 * description }] }`. `mcc` (the 4-digit code) is USUALLY null — Stratus's
 * records carry no code, and the API only fills it when the search itself was
 * an exact 4-digit code. Rows without an `id` are dropped — `id` is what
 * `mccId` wants. Any other shape is an empty list, not a guess.
 */
export function normalizeMccRecords(body) {
  const list = Array.isArray(body?.records) ? body.records : [];
  return list
    .filter((r) => r && r.id !== undefined && r.id !== null && r.id !== "")
    .map((r) => ({
      id: r.id,
      mcc: isFourDigitMcc(r.mcc) ? String(r.mcc) : null,
      category: r.category ?? null,
      description: r.description ?? null,
    }));
}

/** The API rejects an `mcc` that is not exactly 4 digits, so never send one. */
export function isFourDigitMcc(v) {
  return v !== null && v !== undefined && /^\d{4}$/.test(String(v));
}

/** "5999 · Retail — Miscellaneous" ; the code is omitted when Stratus has none. */
export function mccOptionLabel(record) {
  if (!record) return "";
  const text = [record.category, record.description].filter(Boolean).join(" — ");
  return [record.mcc, text].filter(Boolean).join(" · ") || `MCC record ${record.id}`;
}

/** What a selected record contributes to the transmit body. */
export function mccSelectionPayload(record) {
  if (!record) return { mccId: null, mcc: null, mccDescription: null };
  return {
    mccId: record.id,
    mcc: isFourDigitMcc(record.mcc) ? String(record.mcc) : null,
    mccDescription: record.description || record.category || null,
  };
}

/** The full request body for a dry run or a live send. */
export function transmitBody({ mcc, sendForSignature, pricingTemplateId, fileIds } = {}) {
  const hasTemplate = pricingTemplateId !== null && pricingTemplateId !== undefined && pricingTemplateId !== "";
  return {
    ...mccSelectionPayload(mcc),
    sendForSignature: Boolean(sendForSignature),
    // SOUND-784: omitted when nothing is picked, so the server applies the
    // processor's default rather than refusing an empty id.
    ...(hasTemplate ? { pricingTemplateId } : {}),
    // SOUND-830: the ticked documents. Omitted (undefined) = the server sends
    // every eligible document; [] is a real answer (send none).
    ...(Array.isArray(fileIds) ? { fileIds } : {}),
  };
}

/** Don't search on one keystroke; Stratus is paced at 20 requests a minute. */
export const MCC_MIN_SEARCH = 2;
export function shouldSearchMcc(query) {
  return typeof query === "string" && query.trim().length >= MCC_MIN_SEARCH;
}

// ── Responses ──────────────────────────────────────────────────────────────

/**
 * Stratus field errors → `[{ field, label, messages: [] }]`.
 * Accepts the Laravel-style `{ field: ["msg"] }` object and an array of
 * `{ field, message }`; anything else yields an empty list rather than a dump.
 */
export function normalizeUpstreamErrors(response, ownerNames = []) {
  const errors = response?.errors;
  const out = [];
  if (Array.isArray(errors)) {
    for (const e of errors) {
      if (typeof e === "string") out.push({ field: null, label: "General", messages: [plainText(e)] });
      else if (e && typeof e === "object") {
        const msg = e.message || e.error || e.detail;
        if (msg) out.push({ field: e.field || null, label: e.field ? fieldLabel(e.field, ownerNames) : "General", messages: [plainText(msg)] });
      }
    }
  } else if (errors && typeof errors === "object") {
    for (const [field, v] of Object.entries(errors)) {
      const msgs = (Array.isArray(v) ? v : [v]).filter((m) => typeof m === "string" && m.trim()).map(plainText);
      if (msgs.length) out.push({ field, label: fieldLabel(field, ownerNames), messages: msgs });
    }
  }
  return out;
}

/**
 * Classify a failed dry run or live send into what the dialog shows.
 *
 * @returns {{ kind, title, message, fieldErrors, refusal }}
 *   kind:
 *     "already-transmitted"  409 carrying externalApplicationId — it is at Kurv
 *     "status-blocked"       409 — the status cannot move to submitted
 *     "claim-held"           409 — another transmit holds the claim
 *     "incomplete"           422 from OUR preflight (re-render blocking)
 *     "rejected"             400/422 carrying `response` — Stratus refused it
 *     "invalid-input"        400 { error, field } — a malformed mccId / mcc
 *     "not-kurv"             400 { error, processor }
 *     "upstream"             502/503/504
 *     "unknown"              anything else
 *   refusal: a body carrying a fresh `missing` list to merge into the preflight,
 *            or null
 */
export function describeTransmitError(status, body, ownerNames = []) {
  const error = plainText(body?.error);
  const fieldErrors = normalizeUpstreamErrors(body?.response, ownerNames);
  const upstreamMessage = plainText(body?.response?.message);
  const hasMissing = Array.isArray(body?.missing);

  if (status === 409) {
    // Three different refusals share 409. Only one of them means "it is at Kurv".
    if (body?.externalApplicationId) {
      return {
        kind: "already-transmitted",
        title: "Already submitted to Kurv",
        message: [
          "This application was already transmitted",
          body?.transmittedAt ? ` on ${formatDateTime(body.transmittedAt)}` : "",
          ". Sending it again would create a second deal at Kurv. Withdraw it and start a new application instead.",
        ].join(""),
        fieldErrors: [],
        refusal: null,
      };
    }
    const claim = hasMissing && body.missing.some((m) => m?.field === "transmit");
    const isClaim = claim || /another transmit/i.test(error);
    return {
      kind: isClaim ? "claim-held" : "status-blocked",
      title: isClaim ? "Another transmit is in progress" : "Not sent — the status does not allow it",
      message: `${error || "Refused."} Nothing was sent and this record was not changed.`,
      fieldErrors: [],
      refusal: hasMissing ? body : null,
    };
  }

  // A 422 is EITHER our own preflight refusing (it carries `missing`) OR Stratus
  // rejecting the payload (it carries `response`). They need different screens.
  if (status === 422 && hasMissing && !body?.response) {
    return {
      kind: "incomplete",
      title: "Not sent — the application is incomplete",
      message: "Nothing was sent to Kurv. Resolve the blocking items below and try again.",
      fieldErrors: [],
      refusal: body,
    };
  }

  if ((status === 400 || status === 422) && body?.response) {
    return {
      kind: "rejected",
      title: "Kurv rejected the application",
      message:
        upstreamMessage ||
        "Kurv's system refused the submission. Nothing was created. Fix the fields below and try again.",
      fieldErrors,
      refusal: null,
    };
  }

  if (status === 400 && body?.field) {
    return {
      kind: "invalid-input",
      title:
        body.field === "pricingTemplateId"
          ? "The pricing template was not accepted"
          : body.field === "fileIds"
          ? "The document selection was not accepted"
          : "The MCC selection was not accepted",
      message:
        error ||
        (body.field === "pricingTemplateId"
          ? "Pick a pricing template again — it may have been deactivated."
          : body.field === "fileIds"
          ? "A selected document is no longer available. Re-check and choose the documents again."
          : "Pick the MCC again from the search results."),
      fieldErrors: [{ field: body.field, label: INPUT_LABELS[body.field] || humanizeKey(body.field), messages: [error] }],
      refusal: null,
    };
  }

  if (status === 400 && body && "processor" in body) {
    return {
      kind: "not-kurv",
      title: "This is not a Kurv application",
      message: error || "Only MID applications at Kurv can be transmitted.",
      fieldErrors: [],
      refusal: null,
    };
  }

  if (status === 502 || status === 503 || status === 504) {
    return {
      kind: "upstream",
      title: "Kurv could not be reached",
      message:
        error ||
        "The submission did not complete. Check the Kurv portal before trying again — do not retry blindly.",
      fieldErrors,
      refusal: null,
    };
  }

  return {
    kind: "unknown",
    title: "Transmit failed",
    message: error || "Something went wrong. Nothing on this page was changed.",
    fieldErrors,
    refusal: null,
  };
}

/** Labels for the transmit REQUEST's own fields (400 { field }). */
const INPUT_LABELS = {
  fileIds: "Documents",
  mccId: "MCC",
  mcc: "MCC code",
  mccDescription: "MCC description",
  pricingTemplateId: "Pricing template",
};

/**
 * Document-upload results, from either the live 201 (`documents`) or the
 * stored record (`documentUpload`):
 *   uploaded [{ type, filename, stratusCategory }]
 *   errors   [{ type, filename, stratusCategory, error, status }]
 *   skipped  [{ type, filename, reason }]   never sent — no Kurv category for it
 * Strings are tolerated in every list.
 */
export function normalizeDocumentResults(docs) {
  const list = (k) => (Array.isArray(docs?.[k]) ? docs[k] : []);
  return {
    uploaded: list("uploaded").map((d) =>
      typeof d === "string"
        ? { filename: d, type: null, stratusCategory: null }
        : { filename: d?.filename || null, type: d?.type || null, stratusCategory: d?.stratusCategory || null }
    ),
    errors: list("errors").map((e) =>
      typeof e === "string"
        ? { filename: null, type: null, stratusCategory: null, message: plainText(e) }
        : {
            filename: e?.filename || null,
            type: e?.type || null,
            stratusCategory: e?.stratusCategory || null,
            message: plainText(e?.error || e?.message || e?.detail || "Upload failed"),
          }
    ),
    skipped: normalizeSkipped(list("skipped")),
    uploadedAt: docs?.uploadedAt || null,
  };
}

/** `documentsSkipped` on the dry run / `skipped` on the results. */
export function normalizeSkipped(items) {
  return (Array.isArray(items) ? items : []).map((d) =>
    typeof d === "string"
      ? { filename: d, type: null, reason: null }
      : { filename: d?.filename || null, type: d?.type || null, reason: d?.reason ? plainText(d.reason) : null }
  );
}

/** The success screen's facts. */
export function describeTransmitSuccess(body) {
  return {
    // Two Kurv links (CPO): the AGENT link is what we open; the MERCHANT link
    // (`applicationUrl`) is the one a merchant uses to review and sign — copy
    // only. No agent link ⇒ no primary button, never the merchant link instead.
    agentUrl: body?.agentUrl || null,
    merchantUrl: body?.applicationUrl || null,
    editslug: body?.editslug || null,
    signatureSent: Boolean(body?.signatureSent),
    documents: normalizeDocumentResults(body?.documents),
    warningCount: Array.isArray(body?.warnings) ? body.warnings.length : 0,
    // The deal WAS created at Kurv, but the CRM status write failed and the
    // record still reads its previous status. Someone must set it by hand.
    statusNotUpdated: Boolean(body?.statusError),
    statusError: body?.statusError ? plainText(body.statusError) : null,
    status: body?.status || null,
  };
}

/** The rail block for a transmitted record. Null when not transmitted. */
export function transmittedSummary(application) {
  if (!isTransmitted(application)) return null;
  const mccText = [application.mcc, application.mccDescription].filter(Boolean).join(" · ");
  return {
    transmittedAt: application.transmittedAt || null,
    // Agent link opens; merchant link is copy-only. Both internal-only on the
    // API, so either may be absent.
    agentUrl: application.externalAgentUrl || null,
    merchantUrl: application.externalApplicationUrl || null,
    externalId: application.externalApplicationId,
    mcc: mccText || null,
    // SOUND-784 — internal-only on the API; absent for other callers.
    pricingTemplate:
      application.pricingTemplateName || application.pricingTemplateId
        ? [application.pricingTemplateName, application.pricingTemplateId ? `(${application.pricingTemplateId})` : null]
            .filter(Boolean)
            .join(" ")
        : null,
    documents: application.documentUpload ? normalizeDocumentResults(application.documentUpload) : null,
  };
}

export function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * SOUND-784 — the MCC search failed. Say WHY: the API's own `error` when it
 * sent one (e.g. 503 "No Stratus token available…" when the server has no
 * Stratus credential), else distinguish a server/config failure from a blip.
 */
export function describeMccSearchError(status, body) {
  const error = plainText(body?.error);
  if (status === 502 || status === 503 || status === 504) {
    return error
      ? `Kurv's MCC lookup is unavailable: ${error}`
      : `Kurv's MCC lookup is unavailable (${status}) — the server may not be configured for Kurv. Ask engineering.`;
  }
  if (error) return `MCC search failed: ${error}`;
  if (!status) return "Could not reach the server — check your connection and try again.";
  return `MCC search failed (${status}).`;
}

/**
 * SOUND-784 — a dry run refused the template id (400 { field:
 * "pricingTemplateId" }): unknown, or deactivated since the select was filled.
 * The dialog clears its pick, shows the message and re-checks without one.
 */
export function isStaleTemplateError(status, body) {
  return status === 400 && body?.field === "pricingTemplateId";
}
